import { type RefObject, useLayoutEffect } from "react";
import {
	fitPopoverInViewport,
	POPOVER_DEFAULT_OFFSET_PX,
	type PopoverFitAlign,
} from "./fit-popover-in-viewport";

const POPOVER_FIT_MAX_RETRIES = 10;

export type UseFitPopoverInViewportParams = {
	isOpen: boolean;
	popoverRef: RefObject<HTMLElement | null>;
	triggerRef: RefObject<HTMLElement | null>;
	offsetPx?: number;
	align?: PopoverFitAlign;
};

export function useFitPopoverInViewport({
	isOpen,
	popoverRef,
	triggerRef,
	offsetPx = POPOVER_DEFAULT_OFFSET_PX,
	align = "start",
}: UseFitPopoverInViewportParams): void {
	useLayoutEffect(() => {
		if (!isOpen) {
			return;
		}

		let frameId = 0;
		let retryCount = 0;
		let isFitting = false;
		let observer: MutationObserver | null = null;

		const scheduleFit = () => {
			cancelAnimationFrame(frameId);
			frameId = requestAnimationFrame(() => {
				const element = popoverRef.current;

				if (!element) {
					if (retryCount < POPOVER_FIT_MAX_RETRIES) {
						retryCount += 1;
						scheduleFit();
					}
					return;
				}

				retryCount = 0;

				if (!observer) {
					observer = new MutationObserver(() => {
						if (!isFitting) {
							scheduleFit();
						}
					});
					observer.observe(element, {
						attributeFilter: ["style"],
						attributes: true,
					});
				}

				isFitting = true;
				fitPopoverInViewport(element, triggerRef.current, offsetPx, align);
				isFitting = false;
			});
		};

		scheduleFit();
		window.addEventListener("resize", scheduleFit);
		window.addEventListener("scroll", scheduleFit, true);
		window.visualViewport?.addEventListener("resize", scheduleFit);
		window.visualViewport?.addEventListener("scroll", scheduleFit);

		return () => {
			cancelAnimationFrame(frameId);
			observer?.disconnect();
			window.removeEventListener("resize", scheduleFit);
			window.removeEventListener("scroll", scheduleFit, true);
			window.visualViewport?.removeEventListener("resize", scheduleFit);
			window.visualViewport?.removeEventListener("scroll", scheduleFit);
		};
	}, [align, isOpen, offsetPx, popoverRef, triggerRef]);
}
